import {Component, EventEmitter, Input, OnInit, Optional, Output} from '@angular/core';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {languages} from '@ngmedax/cloud-translator';

import {MasterDataLayoutService} from '../../services/master-data-layout.service';
import {TRANSLATION_SEARCH_SCOPE} from '../../../../constants';
import {KEYS} from '../../../../translation-keys';
import {MasterDataLayout, MasterDataFieldPosition, CustomFieldDefinition} from '../../../../types';


// hack to inject decorator declarations
export interface ClientSearchComponent extends Translatable {}

/**
 * Reusable client search component based on master data layout.
 * Renders dynamic search fields for standard and custom fields.
 * Supports ng-content for additional grid-specific search fields.
 */
@Component({
  selector: 'ngmedax-client-search',
  templateUrl: './client-search.component.html',
  styleUrls: ['./client-search.component.css']
})
@Translatable({scope: TRANSLATION_SEARCH_SCOPE, keys: KEYS})
export class ClientSearchComponent implements OnInit {
  /**
   * Filter values for standard fields (two-way binding).
   */
  @Input() public filter: {[key: string]: any} = {};
  @Output() public filterChange = new EventEmitter<{[key: string]: any}>();

  /**
   * Filter values for custom fields (two-way binding).
   */
  @Input() public customFilter: {[key: string]: any} = {};
  @Output() public customFilterChange = new EventEmitter<{[key: string]: any}>();

  /**
   * Emits when any filter value changes.
   */
  @Output() public searchChange = new EventEmitter<void>();

  /**
   * Effective layout (custom or default).
   */
  public effectiveLayout: MasterDataLayout = {gridSize: {cols: 5, rows: 5}, positions: []};

  /**
   * Searchable field positions from the effective layout.
   */
  public searchablePositions: MasterDataFieldPosition[] = [];

  /**
   * Custom field definitions.
   */
  public customFieldDefinitions: CustomFieldDefinition[] = [];

  /**
   * Available languages for locale dropdown.
   */
  public languages = languages;

  /**
   * Raw layout from API (null if using default).
   */
  private masterDataLayout: MasterDataLayout | null = null;

  /**
   * Injects dependencies.
   */
  public constructor(
    private masterDataLayoutService: MasterDataLayoutService,
    @Optional() private translationService: TranslationService
  ) {
  }

  /**
   * Loads layout and custom field definitions on init.
   */
  public async ngOnInit() {
    await this.loadMasterDataLayout();
  }

  /**
   * Handles filter value change.
   */
  public onFilterChange(): void {
    this.filterChange.emit(this.filter);
    this.customFilterChange.emit(this.customFilter);
    this.searchChange.emit();
  }

  /**
   * Returns the CSS grid-template-columns value for the search grid.
   */
  public getSearchGridTemplateColumns(): string {
    return this.masterDataLayoutService.getGridTemplateColumns(this.effectiveLayout);
  }

  /**
   * Returns the CSS grid-column style for a field position.
   */
  public getSearchGridColumn(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridColumn(position);
  }

  /**
   * Returns the CSS grid-row style for a field position.
   */
  public getSearchGridRow(position: MasterDataFieldPosition): string {
    return this.masterDataLayoutService.getGridRow(position);
  }

  /**
   * Returns the label for a search field based on its position.
   */
  public getSearchFieldLabel(position: MasterDataFieldPosition): string {
    if (position.fieldType === 'custom') {
      const def = this.customFieldDefinitions.find(d => d.key === position.fieldKey);
      return def ? def.label : position.fieldKey;
    }
    const labelKey = this.masterDataLayoutService.getStandardFieldLabelKey(position.fieldKey);
    return this._(KEYS.SEARCH[labelKey]) || position.fieldKey;
  }

  /**
   * Returns the custom field definition for a given key.
   */
  public getCustomFieldDefinition(key: string): CustomFieldDefinition | undefined {
    return this.customFieldDefinitions.find(d => d.key === key);
  }

  /**
   * Checks if a standard field should be rendered as dropdown.
   */
  public isDropdownField(fieldKey: string): boolean {
    const dropdownFields = ['gender', 'allowMail', 'prefill', 'locale'];
    return dropdownFields.indexOf(fieldKey) !== -1;
  }

  /**
   * Returns object keys for iteration.
   */
  public objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  /**
   * Translates a language name.
   */
  public translateLanguage(language: string): string {
    return this.translationService ?
      this.translationService.translate({
        text: language,
        scope: ['cloudTranslatorModule', 'default']
      }) : language;
  }

  /**
   * Loads master data layout configuration from API and builds searchable positions.
   */
  private async loadMasterDataLayout(): Promise<void> {
    try {
      const [layout, definitions] = await Promise.all([
        this.masterDataLayoutService.loadLayout(),
        this.masterDataLayoutService.loadCustomFieldDefinitions()
      ]);

      this.masterDataLayout = layout;
      this.customFieldDefinitions = definitions;
    } catch (e) {
      console.error('Failed to load master data layout', e);
      this.masterDataLayout = null;
      this.customFieldDefinitions = [];
    }

    this.effectiveLayout = this.masterDataLayoutService.getEffectiveLayout(this.masterDataLayout);
    this.searchablePositions = this.masterDataLayoutService.getSearchableFields(this.effectiveLayout);
  }
}
